import { Check } from "lucide-react";
import { INTERVIEW_DEFINITION, INTERVIEW_FIELD_MIN_WORDS } from "@mycompbox/shared";
import { ProgressBar, SectionCard } from "@/components/new-look/blocks";
import type { EpicRequest } from "@/lib/api/epic-requests";
import { cn } from "@/lib/utils";

/**
 * How clear an idea is (docs/new-look/PLAN.md, step 4.6): today's compliance
 * score as a clarity meter, and which parts of the interview are covered.
 */

/** Plain words for each interview section. */
const SECTION_WORDS: Record<string, string> = {
  problemStatement: "The problem",
  proposedSolution: "The idea",
  impactAssessment: "Why it matters",
  successMetrics: "How we'll know it worked",
  targetAudience: "Who it's for",
  technicalContext: "Technical details",
  alternatives: "Other options",
  database: "Database",
};

export type SectionState = "done" | "thin" | "missing";

function words(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/** Whether each interview section is covered in the saved request. */
export function sectionStates(
  idea: EpicRequest
): { key: string; label: string; required: boolean; state: SectionState }[] {
  const sd = (idea.structuredDesc ?? {}) as unknown as Record<string, unknown>;
  return INTERVIEW_DEFINITION.map((section) => {
    const texts = section.fields.map((f) => (typeof sd[f] === "string" ? sd[f] : ""));
    const filled = texts.filter((t) => t.trim().length > 0);
    let state: SectionState =
      filled.length === 0 ? "missing" : filled.length < texts.length ? "thin" : "done";
    if (state === "done") {
      for (const field of section.fields) {
        const min = (INTERVIEW_FIELD_MIN_WORDS as Record<string, number | undefined>)[field];
        if (min !== undefined && words(String(sd[field])) < min) state = "thin";
      }
    }
    return {
      key: section.key,
      label: SECTION_WORDS[section.key] ?? section.title,
      required: section.required,
      state,
    };
  });
}

const TIER: Record<string, { label: string; tone: string }> = {
  pass: { label: "Clear enough to plan", tone: "text-green-700 dark:text-green-300" },
  advisory: { label: "Nearly there", tone: "text-amber-700 dark:text-amber-300" },
  "critical-fail": { label: "Needs more detail", tone: "text-red-700 dark:text-red-300" },
};

export function ClarityPanel({ idea }: { idea: EpicRequest }) {
  const score = idea.complianceScore;
  const tier = score ? TIER[score.tier] : undefined;
  const sections = sectionStates(idea);
  const issues = score?.issues.filter((i) => i.tier === "critical").slice(0, 3) ?? [];

  return (
    <SectionCard title="Clarity">
      <div className="flex items-baseline justify-between gap-3">
        <span
          className={cn("text-3xl font-bold tabular-nums", tier?.tone ?? "text-muted-foreground")}
        >
          {score ? score.overall : "—"}
        </span>
        <span className={cn("text-sm font-medium", tier?.tone ?? "text-muted-foreground")}>
          {tier?.label ?? "Not scored yet"}
        </span>
      </div>
      <ProgressBar value={score?.overall ?? 0} label="Clarity" stage="idea" className="mt-2" />

      <ul aria-label="Interview sections" className="mt-4 flex flex-col gap-1.5">
        {sections.map((s) => (
          <li key={s.key} data-state={s.state} className="flex items-center gap-2 text-sm">
            <span
              aria-hidden="true"
              className={cn(
                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                s.state === "done"
                  ? "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200"
                  : s.state === "thin"
                    ? "bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200"
                    : "border border-border text-muted-foreground"
              )}
            >
              {s.state === "done" ? <Check className="h-3 w-3" /> : s.state === "thin" ? "…" : ""}
            </span>
            <span
              className={cn(
                "min-w-0 flex-1",
                s.state === "missing" ? "text-muted-foreground" : "text-foreground"
              )}
            >
              {s.label}
            </span>
            <span className="text-xs text-muted-foreground">
              {s.state === "done"
                ? "covered"
                : s.state === "thin"
                  ? "needs more"
                  : s.required
                    ? "needed"
                    : "optional"}
            </span>
          </li>
        ))}
      </ul>

      {issues.length > 0 && (
        <div className="mt-4 border-t border-border pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            To make it clearer
          </p>
          <ul className="mt-1.5 flex list-disc flex-col gap-1 pl-4 text-sm text-foreground">
            {issues.map((i) => (
              <li key={i.code}>{i.suggestion ?? i.message}</li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}
